import { useCallback, useState } from 'react'
import Project from 'common/project'
import _data from 'common/utils/_data'
import { MemberProfileShortType } from 'common/types/MemberProfile'
import { useSearchParams } from 'react-router-dom'

interface ProfileResponse {
  count: number
  next?: string
  previous?: string
  results: MemberProfileShortType[]
  metadata: any
}

type EndpointType = 'proSearch' | 'basicSearch'

export const useGetShortProfiles = (endpointType: EndpointType) => {
  const [isShortProfilesLoading, setShortProfilesLoading] =
    useState<boolean>(true)
  const [isMoreLoading, setIsMoreLoading] = useState<boolean>(false)
  const [shortProfiles, setShortProfiles] = useState<MemberProfileShortType[]>(
    [],
  )
  const [metaData, setMetaData] = useState({})
  const [shortProfilePage, setShortProfilePage] = useState(1)
  const [hasMoreShortProfiles, setHasMoreShortProfiles] = useState(true)
  const [totalShortProfiles, setTotalShortProfiles] = useState<number>(0)
  const [searchParams] = useSearchParams()

  const fetchProfiles = useCallback(
    (params: string, page: number, pageSize: number, append: boolean) => {
      const baseUrl = `${Project.liveApi}find-a-lawyers/pro-search/profiles/`
      const searchParams = new URLSearchParams(params)

      searchParams.set('limit', pageSize.toString())
      searchParams.set('offset', (page * pageSize).toString())

      const url = `${baseUrl}?${searchParams.toString()}`

      const body: Record<string, string | string[]> = {}
      const arrayKeys = ['area_ids', 'subarea_ids', 'legal_service_ids']

      for (const [key, value] of searchParams.entries()) {
        if (['limit', 'offset'].includes(key)) continue

        const decodedValue = decodeURIComponent(value)
        if (arrayKeys.includes(key)) {
          body[key] = decodedValue.split(',')
        } else {
          body[key] = decodedValue
        }
      }

      _data
        .post(url, body)
        .then((data: ProfileResponse) => {
          if (data.metadata.hourly_rate.min === data.metadata.hourly_rate.max) {
            data.metadata.hourly_rate.min = -1
            data.metadata.hourly_rate.max = 0
          }
          if (
            data.metadata.regulated_years.min ===
            data.metadata.regulated_years.max
          ) {
            data.metadata.regulated_years.min = -1
            data.metadata.regulated_years.max = 0
          }
          setMetaData(data.metadata)
          setTotalShortProfiles(data.count)
          setShortProfiles((prev) =>
            append ? [...prev, ...data.results] : data.results,
          )
          setHasMoreShortProfiles(!!data.next)
          setShortProfilePage(page)
        })
        .catch((error) => console.error(error))
        .finally(() => {
          setShortProfilesLoading(false)
          setIsMoreLoading(false)
        })
    },
    [endpointType],
  )

  const loadShortProfiles = useCallback(
    (params: string, pageSize = 10) => {
      setShortProfilesLoading(true)
      fetchProfiles(params, 0, pageSize, false)
    },
    [fetchProfiles],
  )

  const loadMoreShortProfiles = useCallback(
    (pageSize = 10) => {
      if (!hasMoreShortProfiles) return
      fetchProfiles(
        searchParams.toString(),
        shortProfilePage + 1,
        pageSize,
        true,
      )
    },
    [fetchProfiles, hasMoreShortProfiles, shortProfilePage, searchParams],
  )

  const updateFavouriteLocally = useCallback((id: string) => {
    setShortProfiles((prev) =>
      prev.map((profile) =>
        profile.id === id
          ? { ...profile, is_favourite: !profile.is_favourite }
          : profile,
      ),
    )
  }, [])

  return {
    loadShortProfiles,
    loadMoreShortProfiles,
    shortProfiles,
    hasMoreShortProfiles,
    isShortProfilesLoading,
    updateFavouriteLocally,
    metaData,
    totalShortProfiles,
    isMoreLoading,
    setIsMoreLoading,
    shortProfilePage
  }
}
